/**
 * Power supply efficiency: how much of what a PSU takes from the wall reaches the
 * components, the rest leaving as heat.
 *
 * The 80 PLUS programme certifies minimum efficiencies at 20%, 50% and 100% of a
 * unit's rated output. These are the 115 V internal (non-redundant) thresholds:
 * https://www.clearesult.com/80plus/manufacturers/115V-internal
 *
 * 230 V mains runs a couple of points more efficient, so on European or Brazilian
 * 220–240 V supplies the real draw is slightly lower than estimated here — the
 * error falls on the cautious side.
 */

export const EFFICIENCY_TIERS = [
  'standard',
  'bronze',
  'silver',
  'gold',
  'platinum',
  'titanium',
] as const
export type EfficiencyTier = (typeof EFFICIENCY_TIERS)[number]

/** The load points 80 PLUS tests at, as fractions of rated output. */
export const CERTIFIED_LOADS = [0.2, 0.5, 1] as const

/** Minimum efficiency at each of CERTIFIED_LOADS. `standard` is plain "80 PLUS". */
export const EFFICIENCY_CURVES: Readonly<
  Record<EfficiencyTier, readonly [number, number, number]>
> = {
  standard: [0.8, 0.8, 0.8],
  bronze: [0.82, 0.85, 0.82],
  silver: [0.85, 0.88, 0.85],
  gold: [0.87, 0.9, 0.87],
  platinum: [0.9, 0.92, 0.89],
  titanium: [0.92, 0.94, 0.9],
}

/** Certification marks, written the same in every language. */
export const TIER_NAMES: Readonly<Record<EfficiencyTier, string>> = {
  standard: '80 PLUS',
  bronze: '80 PLUS Bronze',
  silver: '80 PLUS Silver',
  gold: '80 PLUS Gold',
  platinum: '80 PLUS Platinum',
  titanium: '80 PLUS Titanium',
}

export function isEfficiencyTier(value: unknown): value is EfficiencyTier {
  return EFFICIENCY_TIERS.includes(value as EfficiencyTier)
}

/**
 * Efficiency at a load, interpolated linearly between the certified points.
 *
 * Below 20% the curve is held at its 20% value. Real units fall off there, but no
 * tier except Titanium is certified that low, so there is nothing to interpolate
 * towards; light-use figures are therefore a little optimistic. Above 100% the
 * unit is overloaded, and the full-load figure is the best available.
 */
export function efficiencyAt(tier: EfficiencyTier, loadFraction: number): number {
  const curve = EFFICIENCY_CURVES[tier]
  const [low, mid, high] = CERTIFIED_LOADS
  if (!(loadFraction > low)) return curve[0]
  if (loadFraction >= high) return curve[2]
  if (loadFraction <= mid) return lerp(curve[0], curve[1], (loadFraction - low) / (mid - low))
  return lerp(curve[1], curve[2], (loadFraction - mid) / (high - mid))
}

/** Power drawn from the wall to deliver `dcWatts` to the components. */
export function wallWatts(dcWatts: number, efficiency: number): number {
  return dcWatts / efficiency
}

function lerp(from: number, to: number, t: number): number {
  return from + (to - from) * t
}
